import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";

type CookiePreferences = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
};

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
    marketing: false,
  });
  const [entering, setEntering] = useState(false);

  // Expose reset function globally for footer link
  useEffect(() => {
    (window as unknown as Record<string, unknown>).__resetCookieConsent = () => {
      localStorage.removeItem("cookieConsent");
      localStorage.removeItem("cookiePreferences");
      localStorage.removeItem("cookieBannerVersion");
      setVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setEntering(true));
      });
    };
    return () => {
      delete (window as unknown as Record<string, unknown>).__resetCookieConsent;
    };
  }, []);

  useEffect(() => {
    // v2 redesign — force re-show once for everyone
    const bannerVersion = localStorage.getItem("cookieBannerVersion");
    if (bannerVersion !== "v3") {
      localStorage.removeItem("cookieConsent");
      localStorage.removeItem("cookiePreferences");
      localStorage.setItem("cookieBannerVersion", "v3");
    }

    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      const timer = setTimeout(() => {
        setVisible(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => setEntering(true));
        });
      }, 800);
      return () => clearTimeout(timer);
    }

    if (consent === "customized") {
      const saved = localStorage.getItem("cookiePreferences");
      if (saved) {
        try {
          setPreferences(JSON.parse(saved));
        } catch {
          // ignore
        }
      }
    }
  }, []);

  const saveAndClose = useCallback(
    (consent: string, prefs: CookiePreferences) => {
      localStorage.setItem("cookieConsent", consent);
      localStorage.setItem("cookiePreferences", JSON.stringify(prefs));
      setEntering(false);
      setTimeout(() => setVisible(false), 500);
      setShowPreferences(false);
    },
    [],
  );

  const handleAcceptAll = () => {
    saveAndClose("accepted", {
      essential: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleRejectAll = () => {
    saveAndClose("rejected", {
      essential: true,
      analytics: false,
      marketing: false,
    });
  };

  const handleSavePreferences = () => {
    saveAndClose("customized", { ...preferences });
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === "essential") return;
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!visible) return null;

  return (
    <>
      {/* ---------- Cookie Banner ---------- */}
      <div
        className={`fixed right-4 md:right-6 bottom-4 md:bottom-6 z-[60] transition-all duration-500 ease-out w-[calc(100%-2rem)] sm:w-auto sm:max-w-[420px] ${
          entering
            ? "translate-y-0 opacity-100"
            : "translate-y-4 opacity-0"
        }`}
      >
        <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-[0_4px_32px_rgba(139,59,59,0.06)] border border-[#8B3B3B]/10 overflow-hidden">
          {/* Body */}
          <div className="p-4 md:p-5">
            {/* Title */}
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-7 h-7 rounded-lg bg-[#8B3B3B]/7 flex items-center justify-center shrink-0">
                <i className="ri-shield-check-line text-sm text-[#8B3B3B]" />
              </div>
              <p className="font-playfair text-[14px] font-semibold text-foreground-900">
                Paramètres de confidentialité
              </p>
            </div>

            {/* Text */}
            <p className="text-foreground-500 text-[12px] leading-relaxed mb-4">
              Ce site utilise des cookies afin d&apos;améliorer votre
              navigation, mesurer l&apos;audience et garantir le bon
              fonctionnement du site. Vous pouvez accepter, refuser ou
              personnaliser vos préférences à tout moment.
            </p>

            {/* Buttons */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 rounded-lg bg-[#8B3B3B] hover:bg-[#7A3232] text-white text-[12px] font-medium transition-all whitespace-nowrap cursor-pointer shadow-[0_1px_6px_rgba(139,59,59,0.15)]"
              >
                Tout accepter
              </button>
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 rounded-lg border border-[#8B3B3B]/15 text-foreground-600 hover:text-[#8B3B3B] hover:border-[#8B3B3B]/30 text-[12px] font-medium bg-background-50/80 hover:bg-[#8B3B3B]/3 transition-all whitespace-nowrap cursor-pointer"
              >
                Tout refuser
              </button>
              <button
                onClick={() => setShowPreferences(true)}
                className="px-4 py-2 rounded-lg text-foreground-500 hover:text-[#8B3B3B] text-[12px] font-medium hover:bg-[#8B3B3B]/3 transition-all whitespace-nowrap cursor-pointer"
              >
                Personnaliser
              </button>
            </div>

            {/* Links */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <Link
                to="/politique-de-confidentialite"
                className="text-[11px] text-foreground-400 hover:text-[#8B3B3B] transition-colors cursor-pointer"
              >
                Politique de confidentialité
              </Link>
              <button
                onClick={() => setShowPreferences(true)}
                className="text-[11px] text-foreground-400 hover:text-[#8B3B3B] transition-colors cursor-pointer"
              >
                Gestion des cookies
              </button>
            </div>
          </div>

          {/* Subtle bottom accent line */}
          <div className="h-0.5 bg-gradient-to-r from-[#8B3B3B]/30 via-[#8B3B3B]/10 to-transparent" />
        </div>
      </div>

      {/* ---------- Backdrop ---------- */}
      {showPreferences && (
        <div
          className="fixed inset-0 z-[70] bg-black/15 backdrop-blur-[2px] transition-opacity duration-300"
          onClick={() => setShowPreferences(false)}
        />
      )}

      {/* ---------- Preferences Panel ---------- */}
      {showPreferences && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Personnaliser les cookies"
        >
          {/* Panel */}
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-[0_-8px_40px_rgba(139,59,59,0.06)] sm:shadow-[0_12px_48px_rgba(139,59,59,0.08)] max-w-[500px] w-full p-6 md:p-7 max-h-[85vh] overflow-y-auto animate-slide-up">
            {/* Close */}
            <button
              onClick={() => setShowPreferences(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 hover:bg-background-100 transition-all cursor-pointer"
              aria-label="Fermer"
            >
              <i className="ri-close-line text-lg" />
            </button>

            {/* Title */}
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-[#8B3B3B]/8 flex items-center justify-center shrink-0">
                <i className="ri-shield-star-line text-xl text-[#8B3B3B]" />
              </div>
              <div>
                <h3 className="font-playfair text-lg font-semibold text-foreground-900">
                  Gestion des cookies
                </h3>
                <p className="text-foreground-500 text-[11px] mt-0.5">
                  Conformité nLPD — Loi suisse sur la protection des données
                </p>
              </div>
            </div>

            <p className="text-foreground-600 text-[13px] leading-relaxed mb-6">
              Gérez vos préférences ci-dessous. Les cookies essentiels sont
              nécessaires au fonctionnement du site et ne peuvent pas être
              désactivés.
            </p>

            {/* Categories */}
            <div className="space-y-3 mb-7">
              {/* Essential */}
              <div className="flex items-start gap-3 p-4 bg-background-50 rounded-xl border border-background-200/60">
                <div className="w-8 h-8 rounded-lg bg-[#8B3B3B]/6 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-settings-3-line text-[#8B3B3B]" />
                </div>
                <div className="flex-1">
                  <p className="text-[13px] font-medium text-foreground-900">
                    Cookies essentiels
                  </p>
                  <p className="text-[12px] text-foreground-500 mt-0.5 leading-relaxed">
                    Requis pour le bon fonctionnement du site. Ils ne peuvent
                    pas être désactivés.
                  </p>
                </div>
                <div className="w-10 h-6 flex items-center justify-center shrink-0 mt-1">
                  <div className="relative w-9 h-5 rounded-full bg-[#8B3B3B]/15 cursor-default">
                    <div className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-[#8B3B3B]/50 shadow-sm" />
                  </div>
                </div>
              </div>

              {/* Analytics */}
              <button
                onClick={() => togglePreference("analytics")}
                className="w-full text-left flex items-start gap-3 p-4 bg-background-50 rounded-xl border border-background-200/60 hover:border-[#8B3B3B]/25 transition-all cursor-pointer"
              >
                <div className="w-8 h-8 rounded-lg bg-[#8B3B3B]/6 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-bar-chart-line text-[#8B3B3B]" />
                </div>
                <div className="flex-1">
                  <p className="text-[13px] font-medium text-foreground-900">
                    Cookies analytiques
                  </p>
                  <p className="text-[12px] text-foreground-500 mt-0.5 leading-relaxed">
                    Nous aident à comprendre comment vous utilisez le site afin
                    de l&apos;améliorer.
                  </p>
                </div>
                <div className="w-10 h-6 flex items-center justify-center shrink-0 mt-1">
                  <div
                    className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${
                      preferences.analytics
                        ? "bg-[#8B3B3B]/60"
                        : "bg-foreground-200"
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                        preferences.analytics
                          ? "translate-x-[18px]"
                          : "translate-x-0.5"
                      }`}
                    />
                  </div>
                </div>
              </button>

              {/* Marketing */}
              <button
                onClick={() => togglePreference("marketing")}
                className="w-full text-left flex items-start gap-3 p-4 bg-background-50 rounded-xl border border-background-200/60 hover:border-[#8B3B3B]/25 transition-all cursor-pointer"
              >
                <div className="w-8 h-8 rounded-lg bg-[#8B3B3B]/6 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-megaphone-line text-[#8B3B3B]" />
                </div>
                <div className="flex-1">
                  <p className="text-[13px] font-medium text-foreground-900">
                    Cookies marketing
                  </p>
                  <p className="text-[12px] text-foreground-500 mt-0.5 leading-relaxed">
                    Permettent de vous proposer des contenus et offres adaptés à
                    vos centres d&apos;intérêt.
                  </p>
                </div>
                <div className="w-10 h-6 flex items-center justify-center shrink-0 mt-1">
                  <div
                    className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${
                      preferences.marketing
                        ? "bg-[#8B3B3B]/60"
                        : "bg-foreground-200"
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                        preferences.marketing
                          ? "translate-x-[18px]"
                          : "translate-x-0.5"
                      }`}
                    />
                  </div>
                </div>
              </button>
            </div>

            {/* Save */}
            <button
              onClick={handleSavePreferences}
              className="w-full px-6 py-3 rounded-lg bg-[#8B3B3B] hover:bg-[#7A3232] text-white text-[13px] font-medium transition-all whitespace-nowrap cursor-pointer shadow-[0_2px_12px_rgba(139,59,59,0.15)]"
            >
              Enregistrer mes préférences
            </button>
          </div>
        </div>
      )}
    </>
  );
}